import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Atom, ArrowRight } from "@phosphor-icons/react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const HERO = "https://images.pexels.com/photos/37761980/pexels-photo-37761980.jpeg";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      nav("/explore");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const useDemo = () => { setMode("login"); setEmail("investor@stellarnav.io"); setPassword("Explore@2026"); };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:block w-1/2 relative overflow-hidden">
        <img src={HERO} alt="galaxy" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(3,3,5,0.4), #030305)" }} />
        <div className="absolute bottom-0 left-0 p-12">
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <Atom size={28} weight="duotone" color="#00D8FF" />
            <span className="font-display font-bold text-xl tracking-tighter">STELLAR<span className="text-cyan-400">NAV</span></span>
          </Link>
          <h2 className="font-display font-bold text-4xl tracking-tighter leading-tight max-w-md">Save your place among the stars.</h2>
          <p className="text-white/50 mt-4 max-w-sm">Create an account to bookmark celestial objects and build your personalized exploration sessions.</p>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <Atom size={26} weight="duotone" color="#00D8FF" />
            <span className="font-display font-bold text-lg tracking-tighter">STELLAR<span className="text-cyan-400">NAV</span></span>
          </Link>

          <div className="flex gap-1 mb-8 p-1 rounded-full bg-white/5 w-fit">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`px-5 py-2 rounded-full text-sm transition-colors ${mode === m ? "bg-cyan-400 text-[#030305] font-semibold" : "text-white/60 hover:text-white"}`}
              >
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <h1 className="font-display font-bold text-3xl tracking-tighter">
            {mode === "login" ? "Welcome back." : "Join the mission."}
          </h1>
          <p className="text-white/50 text-sm mt-2">
            {mode === "login" ? "Sign in to access your saved views." : "Create your explorer account."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <Field label="Name" value={name} onChange={setName} testid="input-name" type="text" placeholder="Ada Lovelace" required />
            )}
            <Field label="Email" value={email} onChange={setEmail} testid="input-email" type="email" placeholder="you@cosmos.io" required />
            <Field label="Password" value={password} onChange={setPassword} testid="input-password" type="password" placeholder="••••••••" required />

            {error && <div data-testid="auth-error" className="text-red-400 text-sm">{error}</div>}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300 disabled:opacity-60 transition-colors"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
              {!busy && <ArrowRight size={18} weight="bold" />}
            </button>
          </form>

          <button data-testid="auth-demo" onClick={useDemo} className="mt-5 text-xs text-white/45 hover:text-cyan-300 transition-colors">
            Use investor demo credentials →
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, placeholder, testid, required }) {
  return (
    <div>
      <label className="text-tel text-[9px] text-white/45 block mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-cyan-400/60 transition-colors placeholder:text-white/25"
      />
    </div>
  );
}
